import { useCallback, useEffect, useMemo, useState } from 'react';
import { InventoryItem } from '../../../types/inventory';
import { useCanChangeInventoryLocation } from '../../../hooks/useCanChangeInventoryLocation';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../redux/slices/auth.slice';

export interface RequestLocationOption {
  id: number;
  name: string;
  type?: 'WAREHOUSE' | 'BRANCH';
}

export interface UseInventoryRequestModalProps {
  open: boolean;
  selectedProductIds: string[];
  inventoryItems: InventoryItem[];
  locations: RequestLocationOption[];
  defaultDestinationLocationId?: number | null;
  onRemoveProduct?: (productId: string) => void;
}

export const useInventoryRequestModal = ({
  open,
  selectedProductIds,
  inventoryItems,
  locations,
  defaultDestinationLocationId,
  onRemoveProduct,
}: UseInventoryRequestModalProps) => {
  const currentUser = useSelector(selectCurrentUser);
  const canSelectAnyDestination = useCanChangeInventoryLocation();

  const [activeStep, setActiveStep] = useState(0);
  const [destinationLocationId, setDestinationLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedProducts = useMemo(() => {
    return selectedProductIds
      .map((id) => inventoryItems.find((item) => item.id === id))
      .filter((item): item is InventoryItem => item != null);
  }, [selectedProductIds, inventoryItems]);

  const sourceLocationName = selectedProducts[0]?.locationName ?? '';
  const sourceLocationId = selectedProducts[0]?.locationId ?? null;

  const hasMixedOrigins = useMemo(() => {
    if (selectedProducts.length <= 1) {
      return false;
    }
    const originIds = new Set(
      selectedProducts
        .map((product) => product.locationId)
        .filter((id): id is number => id != null && id > 0),
    );
    return originIds.size > 1;
  }, [selectedProducts]);

  const branchLocations = useMemo(
    () => locations.filter((loc) => loc.type === 'BRANCH'),
    [locations],
  );

  const availableDestinations = useMemo(() => {
    const excludingSource = branchLocations.filter((loc) => loc.id !== sourceLocationId);
    if (canSelectAnyDestination) {
      return excludingSource;
    }
    const primaryId = defaultDestinationLocationId ?? currentUser?.primaryLocationId;
    if (!primaryId) {
      return [];
    }
    return excludingSource.filter((loc) => loc.id === primaryId);
  }, [
    branchLocations,
    canSelectAnyDestination,
    currentUser?.primaryLocationId,
    defaultDestinationLocationId,
    sourceLocationId,
  ]);

  const isSameAsSource = useMemo(() => {
    if (!destinationLocationId) return false;
    return Number(destinationLocationId) === sourceLocationId;
  }, [destinationLocationId, sourceLocationId]);

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setDestinationLocationId('');
    setNotes('');
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) {
      handleReset();
      return;
    }
    const defaultId = defaultDestinationLocationId ?? currentUser?.primaryLocationId;
    if (defaultId) {
      setDestinationLocationId(String(defaultId));
    }
  }, [open, defaultDestinationLocationId, currentUser?.primaryLocationId, handleReset]);

  const handleNext = useCallback(() => {
    setActiveStep((prev) => Math.min(prev + 1, 2));
    setError(null);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setError(null);
  }, []);

  const handleDestinationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDestinationLocationId(event.target.value);
  }, []);

  const handleNotesChange = useCallback((value: string) => {
    setNotes(value);
  }, []);

  const handleRemoveProduct = useCallback(
    (productId: string) => {
      onRemoveProduct?.(productId);
    },
    [onRemoveProduct],
  );

  const getDestinationLocationName = useCallback(() => {
    const location = locations.find((loc) => loc.id.toString() === destinationLocationId);
    return location?.name ?? '';
  }, [destinationLocationId, locations]);

  const canProceedToNext = useCallback(() => {
    if (selectedProducts.length === 0 || hasMixedOrigins) {
      return false;
    }
    if (activeStep === 0) {
      return sourceLocationId != null && sourceLocationId > 0;
    }
    if (activeStep === 1) {
      return (
        destinationLocationId !== '' &&
        Number(destinationLocationId) > 0 &&
        !isSameAsSource &&
        availableDestinations.length > 0
      );
    }
    return (
      sourceLocationId != null &&
      sourceLocationId > 0 &&
      destinationLocationId !== '' &&
      Number(destinationLocationId) > 0 &&
      !isSameAsSource
    );
  }, [
    activeStep,
    availableDestinations.length,
    destinationLocationId,
    hasMixedOrigins,
    isSameAsSource,
    selectedProducts.length,
    sourceLocationId,
  ]);

  return {
    activeStep,
    loading,
    setLoading,
    selectedProducts,
    sourceLocationName,
    sourceLocationId,
    hasMixedOrigins,
    destinationLocationId,
    availableDestinations,
    isSameAsSource,
    notes,
    error,
    setError,
    handleNext,
    handleBack,
    handleReset,
    handleDestinationChange,
    handleNotesChange,
    handleRemoveProduct,
    canProceedToNext,
    getDestinationLocationName,
  };
};
